import { ArrowLeft, Edit, Grid3X3, MapPin, Share2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import {
  ActivityListWidget,
  Badge,
  hasCatalogWidget,
  IconHighlight,
  NotesListWidget,
  Skeleton,
  Tooltip,
  useToast,
  Widget,
  WidgetCardShell,
  WidgetGrid,
  WidgetGridProvider,
} from '@aimsos/ui';
import { DASHBOARD_STATES, WIDGET_PREVIEW_DATA } from '../constants';
import { useDashboardDisplayTitle, useDashboardEditor } from '../hooks';
import { publishIssueLines } from '../utils/publishRejection.js';
import { adaptWidgetsForGrid } from '../utils/widgetAdapter';
import { CanvasWidget, DashboardButton, ShareDashboardModal } from '../components';

/**
 * The legacy seed types the catalogue registry does not draw. Everything else
 * resolves through `CanvasWidget`, so this view shows what the builder and
 * the library showed — see `DashboardNewPage` for the same pairing.
 */
const LEGACY_PREVIEW_REGISTRY = {
  activity: ActivityListWidget,
  notes: NotesListWidget,
};

const DashboardDetailPage = () => {
  const { t } = useTranslation('dashboards');
  const displayTitle = useDashboardDisplayTitle();
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const toast = useToast();
  const {
    descriptionKey,
    description,
    error,
    loading,
    sectionPath,
    sharedWith,
    state,
    titleKey,
    title,
    hasDuplicateTitleSuffix,
    updateEditor,
    widgets,
    publish,
    saving,
  } = useDashboardEditor();
  const navToast = location.state?.toast;

  useEffect(() => {
    if (!navToast) return;
    const opts = {
      title: navToast.title,
      message: navToast.description || undefined,
      canClose: true,
    };
    if (navToast.severity === 'success') toast.success(opts);
    else if (navToast.severity === 'error') toast.error(opts);
    else if (navToast.severity === 'warning') toast.warning(opts);
    else toast.info(opts);
    navigate(location.pathname, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toast API identity changes when the toast queue updates
  }, [navToast, location.pathname, navigate]);

  const handleBack = useCallback(() => navigate('/dashboards'), [navigate]);
  const handleEdit = useCallback(() => {
    if (!dashboardId) return;
    navigate(`/dashboards/${dashboardId}/edit`);
  }, [dashboardId, navigate]);
  const closeShareModal = useCallback(() => setIsShareModalOpen(false), []);
  const handleShareConfirm = useCallback(
    (nextSharedWith) => {
      const resolved = Array.isArray(nextSharedWith) ? nextSharedWith : [];
      updateEditor({ sharedWith: resolved });
      setIsShareModalOpen(false);
      navigate(location.pathname, {
        replace: true,
        state: {
          toast: {
            severity: 'success',
            title: t('editor.toasts.shared'),
            description: t('editor.toasts.sharedDescription'),
          },
        },
      });
    },
    [location.pathname, navigate, t, updateEditor],
  );

  const hasWidgets = widgets.length > 0;
  const showToast = useCallback(
    (severity, toastTitle, description) => {
      const opts = { title: toastTitle, description };
      if (severity === 'success') toast.success(opts);
      else if (severity === 'error') toast.error(opts);
      else if (severity === 'warning') toast.warning(opts);
      else toast.info(opts);
    },
    [toast],
  );

  /*
    Publishing from the viewer (CT-5552).

    A draft opened here could only be published by going back into the
    editor, which is a detour for a dashboard that is already finished: the
    state badge is right there saying "Draft". Same call and same failure
    handling as the editor's button — the hook owns both.
  */
  const handlePublish = useCallback(async () => {
    if (saving) return;
    showToast('warning', t('editor.toasts.publishing'), t('editor.toasts.publishingDescription'));
    const result = await publish();
    if (result?.validationErrors?.length) {
      const message = [
        result.validationErrors.includes('title') ? t('editor.toasts.validationNoTitle') : null,
        result.validationErrors.includes('sectionId') ? t('editor.toasts.validationNoSection') : null,
      ]
        .filter(Boolean)
        .join(' ');
      showToast('error', t('editor.toasts.validationTitle'), message);
      return;
    }
    if (!result?.success) {
      const lines = result?.rejected ? publishIssueLines(result.issues) : [];
      showToast(
        'error',
        t('editor.toasts.publishError'),
        lines.length
          ? t('editor.toasts.publishRejectedDescription', { count: lines.length, issues: lines.join(' · ') })
          : result?.error?.message || t('editor.toasts.publishErrorDescription'),
      );
    }
  }, [publish, saving, showToast, t]);

  const statusLabel = state === DASHBOARD_STATES.PUBLISHED ? t('status.published') : t('status.draft');
  const resolvedTitle = displayTitle({ titleKey, title, hasDuplicateTitleSuffix });
  const resolvedDescription = descriptionKey
    ? t(descriptionKey, { defaultValue: description || '' })
    : description;
  const externalWidgetConfig = useMemo(
    () =>
      adaptWidgetsForGrid(widgets, {
        tooltips: {
          refresh: t('widgetActions.refresh'),
          expand: t('widgetActions.expand'),
          add: t('widgetActions.add'),
        },
      }),
    [t, widgets],
  );

  const renderWidget = useCallback((widgetConfig) => {
    const builderType = widgetConfig._builderType;
    const cardConfig = widgetConfig.config?.card || {};

    let content = null;
    if (hasCatalogWidget(builderType)) {
      content = <CanvasWidget type={builderType} widgetUuid={widgetConfig._widgetUuid} />;
    } else {
      const previewData = WIDGET_PREVIEW_DATA[builderType] ?? null;
      const PreviewComponent = LEGACY_PREVIEW_REGISTRY[builderType];
      if (previewData && PreviewComponent) {
        content = <PreviewComponent data={previewData} isCompressed={false} />;
      } else if (previewData) {
        content = <Widget data={previewData} />;
      }
    }

    return (
      <WidgetCardShell title={cardConfig.title} headerButtons={cardConfig.header_buttons}>
        {content}
      </WidgetCardShell>
    );
  }, []);

  if (loading) {
    return (
      <main className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
          <Skeleton height="56px" width="100%" borderRadius="8px" />
          <Skeleton
            className="min-h-[min(560px,calc(100vh-12rem))] w-full flex-1"
            height={480}
            width="100%"
            borderRadius="8px"
          />
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
          <section className="flex min-h-[223px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-edge px-4 py-8">
            <h2 className="text-base font-semibold text-content">{t('detailPage.notFoundTitle')}</h2>
            <p className="max-w-xl text-sm font-medium text-content-muted">
              {t('detailPage.notFoundDescription')}
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
          <header className="flex shrink-0 items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <DashboardButton variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={handleBack} />
              <div className="min-w-0 space-y-2">
                <div className="flex min-w-0 items-center gap-2">
                  <h1 className="truncate text-lg font-semibold text-content">{resolvedTitle}</h1>
                  <Badge variant={state === DASHBOARD_STATES.PUBLISHED ? 'success' : 'default'}>{statusLabel}</Badge>
                  {sectionPath && (
                    <div className="inline-flex h-7 items-center gap-1 rounded-lg border border-edge-strong bg-surface px-2 py-1">
                      <MapPin size={12} className="text-content-muted" />
                      <span className="text-xs text-content-secondary">{sectionPath}</span>
                    </div>
                  )}
                </div>
                {resolvedDescription && <p className="text-sm text-content-muted">{resolvedDescription}</p>}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Tooltip content={t('share.unavailableTooltip')} size="compact">
                <DashboardButton variant="ghost" size="sm" leftIcon={Share2} disabled />
              </Tooltip>
              {state !== DASHBOARD_STATES.PUBLISHED && (
                <DashboardButton variant="secondary" size="sm" onClick={handlePublish} disabled={saving}>
                  {saving ? t('editor.newPage.publishingButton') : t('editor.newPage.publishButton')}
                </DashboardButton>
              )}
              <DashboardButton size="sm" leftIcon={Edit} onClick={handleEdit}>
                {t('actions.editDashboard')}
              </DashboardButton>
            </div>
          </header>

          {hasWidgets ? (
            <section className="min-h-[260px] flex-1">
              <WidgetGridProvider isEditMode={false}>
                <WidgetGrid externalWidgetConfig={externalWidgetConfig} renderWidget={renderWidget} readOnly />
              </WidgetGridProvider>
            </section>
          ) : (
            <section
              className="flex w-full flex-1 flex-col items-center justify-center gap-4 rounded-lg border border-edge bg-surface px-6 py-12 sm:min-h-[min(560px,calc(100vh-12rem))]"
              aria-labelledby="dashboard-detail-empty-title"
            >
              <div className="flex max-w-md flex-col items-center gap-3 text-center">
                <IconHighlight icon={Grid3X3} bgColor="LightBlue" iconColor="LightBlue" size="medium" />
                <h2 id="dashboard-detail-empty-title" className="text-lg font-semibold text-content">
                  {t('list.noWidgets')}
                </h2>
                <p className="text-sm font-medium text-content-muted">{t('list.noWidgetsDescription')}</p>
              </div>
            </section>
          )}
        </div>
      </main>

      {isShareModalOpen && (
        <ShareDashboardModal
          open={isShareModalOpen}
          onClose={closeShareModal}
          onConfirm={handleShareConfirm}
          dashboardId={dashboardId || 'new'}
          dashboardName={resolvedTitle}
          existingSharedWith={sharedWith}
        />
      )}
    </>
  );
};

export default DashboardDetailPage;
